import React from 'react';
import { Link } from 'react-router-dom';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { FileText, Clock, Target, Mail } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const WarrantyOverviewPage = () => {
  const { theme } = useTheme();
  
  return (
    <AdminPageLayout
      title="🛡️ Warranty System"
      subtitle="Florida-compliant extended HVAC labor warranty management"
      actions={
        <>
          <Link
            to="/warranty/equipment"
            className="px-4 py-2 rounded-lg font-semibold text-white transition-colors"
            style={{ backgroundColor: theme.colors.primaryColor }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
          >
            ➕ Register Equipment
          </Link>
          <Link
            to="/warranty/agreements"
            className="px-4 py-2 rounded-lg font-semibold text-white transition-colors"
            style={{ backgroundColor: theme.colors.successText }}
          >
            📋 Create Agreement
          </Link>
        </>
      }
    >
      {/* Stats Grid */}
      <StatsGrid cols={4}>
        <StatCard label="Active Agreements" value="1,247" icon={FileText} />
        <StatCard label="Pending Authorizations" value="23" icon={Clock} variant="warning" />
        <StatCard label="Claims This Month" value="89" icon={Target} />
        <StatCard label="Contract Delivery" value="99.2%" icon={Mail} variant="success" />
      </StatsGrid>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance Alerts */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            🚨 Compliance Alerts
          </h3>
          <div className="space-y-3">
            <div className="p-3 border rounded-lg" style={{ backgroundColor: theme.colors.warningBg, borderColor: theme.colors.warningBorder }}>
              <div className="flex justify-between items-center">
                <span style={{ color: theme.colors.textPrimary }}>Contracts nearing 45-day deadline</span>
                <span className="font-semibold" style={{ color: theme.colors.warningText }}>3</span>
              </div>
              <p className="text-sm mt-1" style={{ color: theme.colors.textSecondary }}>§634.414 delivery requirement</p>
            </div>
            <div className="p-3 border rounded-lg" style={{ backgroundColor: theme.colors.successBg, borderColor: theme.colors.successBorder }}>
              <div className="flex justify-between items-center">
                <span style={{ color: theme.colors.textPrimary }}>On-time delivery rate</span>
                <span className="font-semibold" style={{ color: theme.colors.successText }}>99.2%</span>
              </div>
              <p className="text-sm mt-1" style={{ color: theme.colors.textSecondary }}>Above 95% compliance threshold</p>
            </div>
            <div className="p-3 border rounded-lg" style={{ backgroundColor: theme.colors.infoBg, borderColor: theme.colors.infoBorder }}>
              <div className="flex justify-between items-center">
                <span style={{ color: theme.colors.textPrimary }}>Reserve balance</span>
                <span className="font-semibold" style={{ color: theme.colors.infoText }}>$2.4M</span>
              </div>
              <p className="text-sm mt-1" style={{ color: theme.colors.textSecondary }}>§634.406 financial responsibility</p>
            </div>
          </div>
        </Card>

        {/* Quick Actions */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            ⚡ Quick Actions
          </h3>
          <div className="space-y-3">
            <Link
              to="/warranty/equipment"
              className="block w-full p-3 rounded-lg transition-colors"
              style={{ backgroundColor: theme.colors.primaryColor, color: 'white' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
            >
              🔧 Register New Equipment
            </Link>
            <Link
              to="/warranty/agreements"
              className="block w-full p-3 rounded-lg transition-opacity hover:opacity-80"
              style={{ backgroundColor: theme.colors.successText, color: 'white' }}
            >
              📋 Create Agreement
            </Link>
            <Link
              to="/warranty/coverage"
              className="block w-full p-3 rounded-lg transition-opacity hover:opacity-80"
              style={{ backgroundColor: theme.colors.infoText, color: 'white' }}
            >
              🔍 Coverage Lookup
            </Link>
            <Link
              to="/warranty/reports"
              className="block w-full p-3 rounded-lg transition-opacity hover:opacity-80"
              style={{ backgroundColor: theme.colors.warningText, color: 'white' }}
            >
              📊 Generate Compliance Report
            </Link>
          </div>
        </Card>
      </div>

      {/* Navigation Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <NavCard
          title="Equipment Registry"
          description="Manage HVAC equipment serial numbers, models, and installation data"
          icon="🔧"
          link="/warranty/equipment"
          stats="1,247 units"
        />
        <NavCard
          title="Warranty Agreements"
          description="Create and manage labor warranty contracts with Florida compliance"
          icon="📋"
          link="/warranty/agreements"
          stats="1,182 active"
        />
        <NavCard
          title="Claims & Authorizations"
          description="Process pre-authorizations and manage contractor claims"
          icon="🎯"
          link="/warranty/claims"
          stats="23 pending"
        />
        <NavCard
          title="Contractor Management"
          description="Manage HVAC contractors, ACH setup, and performance tracking"
          icon="👷"
          link="/warranty/contractors"
          stats="89 contractors"
        />
        <NavCard
          title="Compliance Reports"
          description="Generate Florida OIR reports and compliance documentation"
          icon="📊"
          link="/warranty/reports"
          stats="All compliant"
        />
        <NavCard
          title="Coverage Lookup"
          description="Public tool for contractors to verify warranty coverage"
          icon="🔍"
          link="/warranty/coverage"
          stats="Public access"
        />
      </div>
    </AdminPageLayout>
  );
};

const NavCard = ({
  title,
  description,
  icon,
  link,
  stats
}: {
  title: string;
  description: string;
  icon: string;
  link: string;
  stats: string;
}) => {
  const { theme } = useTheme();
  
  return (
    <Link
      to={link}
      className="p-6 rounded-lg border transition-colors group"
      style={{
        backgroundColor: theme.colors.bgCard,
        borderColor: theme.colors.borderColor
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = theme.colors.primaryColor}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = theme.colors.borderColor}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="text-2xl">{icon}</div>
        <span 
          className="text-xs px-2 py-1 rounded"
          style={{
            color: theme.colors.textTertiary,
            backgroundColor: theme.colors.bgHover
          }}
        >
          {stats}
        </span>
      </div>
      <h3 
        className="text-lg font-semibold mb-2 group-hover:opacity-80 transition-opacity"
        style={{ color: theme.colors.textPrimary }}
      >
        {title}
      </h3>
      <p className="text-sm leading-relaxed" style={{ color: theme.colors.textSecondary }}>
        {description}
      </p>
    </Link>
  );
};

export default WarrantyOverviewPage;
